/**
 * Unified Current Project Hook
 *
 * Provides a single source of truth for current project management using TanStack Query.
 * Similar pattern to useCurrentOrganization but for project context in AI Assistant.
 *
 * Features:
 * - Current project stored in localStorage
 * - TanStack Query as single source of truth
 * - Automatic project validation against current organization
 * - Type-safe project operations
 *
 * @version 1.0.0
 * @last-updated 2026-02-18
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { projectService } from "@/services/core/project.service";
import { useCurrentOrganization } from "./useCurrentOrganization";
import { toast } from '@/components/ui/sonner';
import type { Project } from "@flowstate/types";
import { logger } from "@/lib/logger";

const STORAGE_KEY = "flowstate_current_project_id";

/**
 * Hook return interface
 */
export interface UseCurrentProjectReturn {
  /** Current active project */
  currentProject: Project | null;
  /** All projects in current organization */
  projects: Project[];
  /** Loading state for projects list */
  isLoading: boolean;
  /** Error from projects query */
  error: Error | null;
  /** Set a project as current */
  setCurrentProject: (projectId: string | null) => Promise<void>;
  /** Is a set operation in progress */
  isSetting: boolean;
  /** Refresh projects list */
  refreshProjects: () => Promise<void>;
  /** Clear current project */
  clearCurrentProject: () => void;
}

/**
 * Get current project ID from localStorage
 */
function getCurrentProjectId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    logger.error("Error reading from localStorage:", { error: error });
    return null;
  }
}

/**
 * Set current project ID in localStorage
 */
function setCurrentProjectIdStorage(projectId: string | null): void {
  try {
    if (projectId) {
      localStorage.setItem(STORAGE_KEY, projectId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    logger.error("Error writing to localStorage:", { error: error });
  }
}

/**
 * Unified hook for current project management
 *
 * Uses TanStack Query as the single source of truth for project state.
 * LocalStorage is used only for persistence across sessions.
 * Only shows projects from the current organization.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const {
 *     currentProject,
 *     projects,
 *     setCurrentProject,
 *     isLoading
 *   } = useCurrentProject();
 *
 *   if (isLoading) return <Loading />;
 *
 *   return (
 *     <div>
 *       <h1>{currentProject?.name}</h1>
 *       <select onChange={(e) => setCurrentProject(e.target.value)}>
 *         {projects.map(project => (
 *           <option key={project.id} value={project.id}>{project.name}</option>
 *         ))}
 *       </select>
 *     </div>
 *   );
 * }
 * ```
 */
export function useCurrentProject(): UseCurrentProjectReturn {
  const queryClient = useQueryClient();
  const { currentOrganization } = useCurrentOrganization();
  const [currentProjectId, setCurrentProjectIdState] = useState<string | null>(() => getCurrentProjectId());

  // Fetch all projects for current organization
  const {
    data: projects = [],
    isLoading,
    error,
    refetch: refreshProjects,
  } = useQuery({
    queryKey: ["projects", "current-org", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) {
        return [];
      }
      // organization_id is passed as second parameter, not in filters
      const response = await projectService.getProjects(undefined, currentOrganization.id);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data || [];
    },
    enabled: !!currentOrganization?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Determine current project
  // Priority: 1) Stored ID if valid and in current org, 2) First project in list, 3) null
  const currentProject = (() => {
    if (!projects.length) {return null;}

    // If we have a stored ID and it exists in the list, use it
    if (currentProjectId) {
      const stored = projects.find((project) => project.id === currentProjectId);
      if (stored) {return stored;}
    }

    // Otherwise use first project and update storage
    const firstProject = projects[0];
    if (firstProject && firstProject.id !== currentProjectId) {
      setCurrentProjectIdStorage(firstProject.id);
      setCurrentProjectIdState(firstProject.id);
    }
    return firstProject || null;
  })();

  // Set project mutation with optimistic updates
  const setMutation = useMutation({
    mutationFn: async (projectId: string | null) => {
      if (projectId === null) {
        return null;
      }
      // Validate project exists in current org's list
      const project = projects.find((p) => p.id === projectId);
      if (!project) {
        throw new Error("Project not found or you don't have access");
      }
      return project;
    },
    onMutate: async (projectId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["projects", "current-org"] });

      // Save previous state for rollback
      const previousProjectId = currentProjectId;

      // Optimistically update state
      setCurrentProjectIdStorage(projectId);
      setCurrentProjectIdState(projectId);

      return { previousProjectId };
    },
    onError: (err, _projectId, context) => {
      // Rollback on error
      if (context?.previousProjectId) {
        setCurrentProjectIdStorage(context.previousProjectId);
        setCurrentProjectIdState(context.previousProjectId);
      }

      toast.error("Failed to set project", { description: err instanceof Error ? err.message : "Unknown error occurred" });
    },
    onSuccess: (project) => {
      if (project) {
        toast.success("Project context updated", { description: `Now working in ${project.name}` });
      }
    },
  });

  // Clear current project when organization changes (if project doesn't belong to new org)
  useEffect(() => {
    if (currentProjectId && projects.length > 0) {
      const stillValid = projects.some((p) => p.id === currentProjectId);
      if (!stillValid) {
        // Clear the stored project ID since it's not in the new org
        setCurrentProjectIdStorage(null);
        setCurrentProjectIdState(null);
      }
    }
  }, [currentOrganization?.id, currentProjectId, projects]);

  // Sync localStorage with state when project changes
  useEffect(() => {
    if (currentProject && currentProject.id !== currentProjectId) {
      setCurrentProjectIdStorage(currentProject.id);
      setCurrentProjectIdState(currentProject.id);
    }
  }, [currentProject, currentProjectId]);

  const clearCurrentProject = () => {
    setCurrentProjectIdStorage(null);
    setCurrentProjectIdState(null);
    toast.success("Project context cleared", { description: "No project selected" });
  };

  return {
    currentProject,
    projects,
    isLoading,
    error: error instanceof Error ? error : null,
    setCurrentProject: async (projectId: string | null) => {
      await setMutation.mutateAsync(projectId);
    },
    isSetting: setMutation.isPending,
    refreshProjects: async () => {
      await refreshProjects();
    },
    clearCurrentProject,
  };
}

/**
 * Hook to check if a specific project is currently active
 *
 * @param projectId - Project ID to check
 * @returns True if the project is currently active
 *
 * @example
 * ```tsx
 * function ProjectBadge({ projectId }: { projectId: string }) {
 *   const isActive = useIsCurrentProject(projectId);
 *   return <Badge variant={isActive ? "default" : "outline"}>Project</Badge>;
 * }
 * ```
 */
export function useIsCurrentProject(projectId: string): boolean {
  const { currentProject } = useCurrentProject();
  return currentProject?.id === projectId;
}

/**
 * Hook to get current project ID only (lighter weight)
 *
 * @returns Current project ID or null
 *
 * @example
 * ```tsx
 * function useFilteredTasks() {
 *   const currentProjectId = useCurrentProjectId();
 *   return useQuery({
 *     queryKey: ['tasks', currentProjectId],
 *     queryFn: () => fetchTasks(currentProjectId),
 *     enabled: !!currentProjectId,
 *   });
 * }
 * ```
 */
export function useCurrentProjectId(): string | null {
  const { currentProject } = useCurrentProject();
  return currentProject?.id || null;
}
